import React from 'react';
import { cn } from '@/utils/cn';
import { tokens } from '@/theme/tokens';
import { CardProps } from './Card.types';

const CARD_COLOR_CLASSES: Record<string, { bg: string; text: string }> = {
  '#22c55e': { bg: 'bg-green-500', text: 'text-green-500' },
  '#10b981': { bg: 'bg-emerald-500', text: 'text-emerald-500' },
  '#06b6d4': { bg: 'bg-cyan-500', text: 'text-cyan-500' },
  '#3b82f6': { bg: 'bg-sky-500', text: 'text-sky-500' },
  '#14b8a6': { bg: 'bg-teal-500', text: 'text-teal-500' },
  '#0f766e': { bg: 'bg-teal-700', text: 'text-teal-700' },
  '#8b5cf6': { bg: 'bg-emerald-500', text: 'text-emerald-500' },
  '#ef4444': { bg: 'bg-red-500', text: 'text-red-500' },
  '#f43f5e': { bg: 'bg-rose-500', text: 'text-rose-500' },
  '#eab308': { bg: 'bg-amber-500', text: 'text-amber-500' },
  '#f59e0b': { bg: 'bg-amber-500', text: 'text-amber-500' },
  '#64748b': { bg: 'bg-slate-500', text: 'text-slate-500' },
  '#6b7280': { bg: 'bg-gray-500', text: 'text-gray-500' },
};

const getCardColorClasses = (color?: string) =>
  color ? CARD_COLOR_CLASSES[color.toLowerCase()] || null : null;

/**
 * # Card Component
 *
 * The fundamental container component for Rootify UI.
 * Use this for grouping content, widgets, and sections.
 *
 * ## Examples
 *
 * ```tsx
 * // Default Glass Card (most common)
 * <Card>Content</Card>
 *
 * // Interactive Card
 * <Card onClick={handleClick} interactive>Click me</Card>
 *
 * // Feature Card with Glow & Watermark
 * <Card
 *   glowColor="#22c55e"
 *   watermarkIcon={<Leaf />}
 *   status="success"
 * >
 *   Content
 * </Card>
 * ```
 */
export const Card: React.FC<CardProps> = ({
  children,
  variant = 'glass',
  padding = 'md',
  accentColor,
  status,
  statusPosition = 'top-right',
  watermarkIcon,
  glowColor,
  selected = false,
  interactive = false,
  headerAction,
  className,
  onClick,
  onKeyDown,
  style,
  ...props
}) => {
  void style;

  // Base classes for all cards
  const baseClasses = cn(
    'group relative overflow-hidden focus:outline-none',
    tokens.radius.lg,
    tokens.transition.normal,
  );

  // Interactive classes (hover effects)
  const isInteractive = interactive || !!onClick;
  const interactiveClasses = isInteractive
    ? 'cursor-pointer hover:-translate-y-1 hover:shadow-xl active:scale-[0.98] focus-visible:ring-2 focus-visible:ring-emerald-500/30 focus-visible:ring-offset-2 focus-visible:ring-offset-white dark:focus-visible:ring-offset-zinc-950'
    : '';

  // Padding mapping
  const paddingClasses = {
    none: 'p-0',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  }[padding];

  // Variant mapping
  const variantClasses = {
    default:
      'bg-white/92 dark:bg-zinc-950/78 border border-emerald-100/80 dark:border-white/10 shadow-[0_12px_30px_rgba(15,23,42,0.06)] dark:shadow-[0_12px_30px_rgba(0,0,0,0.24)]',

    // Premium Glass Effect
    glass: cn(
      'backdrop-blur-xl backdrop-saturate-[1.12]',
      tokens.surface.cardGlass,
    ),

    outline: 'bg-transparent border border-zinc-200 dark:border-zinc-700',

    ghost: 'bg-transparent border-none shadow-none hover:bg-zinc-100/50 dark:hover:bg-white/5',
  }[variant];

  // Selected State
  const selectedClasses = selected
    ? 'ring-2 ring-offset-2 ring-emerald-500/50 ring-offset-white dark:ring-offset-zinc-950'
    : '';

  const accentTone = getCardColorClasses(accentColor);
  const glowTone = getCardColorClasses(glowColor);

  // Status Colors
  const statusColors: Record<string, string> = {
    success: 'bg-green-500',
    warning: 'bg-yellow-500',
    error: 'bg-red-500',
    neutral: 'bg-gray-500',
    info: 'bg-cyan-500',
    offline: 'bg-gray-400',
  };

  const obsluzKlavesnici: React.KeyboardEventHandler<HTMLDivElement> = (udalost) => {
    onKeyDown?.(udalost);

    if (udalost.defaultPrevented || !isInteractive || !onClick) {
      return;
    }

    if (udalost.key === 'Enter' || udalost.key === ' ') {
      udalost.preventDefault();
      udalost.currentTarget.click();
    }
  };

  return (
    <div
      className={cn(
        baseClasses,
        paddingClasses,
        variantClasses,
        interactiveClasses,
        selectedClasses,
        className,
      )}
      onClick={onClick}
      onKeyDown={obsluzKlavesnici}
      {...(isInteractive ? { role: 'button' as const, tabIndex: 0 } : {})}
      {...props}
    >
      {/* 1. Ambient Background Glow */}
      {glowTone && (
        <div
          className={cn(
            'absolute -top-24 -right-24 h-64 w-64 rounded-full blur-[80px] opacity-10 transition-opacity duration-500 pointer-events-none group-hover:opacity-20',
            glowTone.bg,
          )}
        />
      )}

      {/* 2. Watermark Icon */}
      {watermarkIcon && (
        <div
          className={cn(
            'absolute -bottom-6 -right-6 h-32 w-32 opacity-[0.03] dark:opacity-[0.05] pointer-events-none transition-transform duration-500 group-hover:scale-110 group-hover:rotate-12 origin-center',
            glowTone?.text || 'text-current',
            '[mask-image:linear-gradient(to_bottom_right,black,transparent)]',
            '[-webkit-mask-image:linear-gradient(to_bottom_right,black,transparent)]',
          )}
        >
          {/* We assume the consumer passes an SVG icon component that fills the container or has width/height set */}
          <div className="w-full h-full [&>svg]:w-full [&>svg]:h-full">{watermarkIcon}</div>
        </div>
      )}

      {/* 3. Accent Indicator (Bar) */}
      {accentTone && (
        <div
          className={cn('absolute top-0 left-0 right-0 h-1 opacity-80', accentTone.bg)}
        />
      )}

      {/* 4. Status Indicator */}
      {status && (
        <div
          className={cn(
            'absolute top-4 w-2 h-2 rounded-full shadow-lg ring-2 ring-white dark:ring-zinc-900',
            statusColors[status],
            statusPosition === 'top-right' ? 'right-4' : 'left-4',
          )}
        />
      )}

      {/* 5. Header Action Slot */}
      {headerAction && <div className="absolute top-4 right-4 z-20">{headerAction}</div>}

      {/* 6. Shine effect for glass variant (Subtle) */}
      {variant === 'glass' && isInteractive && (
        <div className="absolute inset-0 opacity-0 hover:opacity-100 transition-opacity duration-700 pointer-events-none z-0">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent -translate-x-full hover:animate-shine" />
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};
